import { globalConfirm } from '../components/ConfirmDialog';
import { useFormatter } from '../utils/formatters';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import InteractiveTable from '../components/InteractiveTable';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download, Upload, X, Trash2, QrCode, Image, Search, Boxes, Package } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DataLookupField from '../components/DataLookupField';
import { useExpenditureLabels, useDecimalFormat } from '../contexts/ExpenditureLabelsContext';
import ModuleReports from './Reports/ModuleReports';
import { ITEMS_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { itemsConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

function SupplierLookupField({ value, onChange }) {
  const [search, setSearch] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => { setSearch(value || ''); }, [value]);

  const doSearch = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/api/data/suppliers', { params: { search: q || '', page_size: 10 } });
        setResults(res.data.data || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input value={search}
          onChange={e => { setSearch(e.target.value); doSearch(e.target.value); }}
          onFocus={() => doSearch(search || '')}
          onBlur={() => setTimeout(() => setOpen(false), 250)}
          className="pr-8" placeholder="Search supplier..." />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-[200] top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-40 overflow-y-auto"
          onMouseDown={e => e.preventDefault()}>
          {results.map(s => (
            <div key={s.supplier_code} className="w-full text-left px-3 py-2 text-sm hover:bg-accent border-b last:border-b-0 cursor-pointer"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onChange(s.supplier_code, s.supplier_name); setSearch(s.supplier_code); setOpen(false); }}>
              <span className="font-mono text-primary text-xs font-medium">{s.supplier_code}</span>
              <span className="text-muted-foreground text-xs ml-2">— {s.supplier_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ITEM_TYPES = ['Consumable', 'Current Asset', 'Fixed Asset'];
const EMPTY_ITEM = { item_code: '', outline: '', description: '', item_type: 'Consumable', category_code: '', upc: '', stock_item: false, batch_tracked: false, serial_tracked: false, account_code_4: '', account_code_5: '', account_code_6: '', active: true };
const EMPTY_SUPPLIER = { supplier_code: '', supplier_name: '', suppliers_part_number: '', purchase_unit: 'EACH', pack_size: 1, unit_price: 0, discount: 0, sales_tax: 0, lead_time: 0, co2e: 0, is_main_supplier: false };

export default function ItemsPage() {
  const [records, setRecords] = useState([]);
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('items');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortState, setSortState] = useState({ field: 'item_code', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ITEM);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemSuppliers, setItemSuppliers] = useState([]);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState(EMPTY_SUPPLIER);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [segments, setSegments] = useState([]);
  const { labels: expLabels } = useExpenditureLabels();
  const { fmtVal, fmtInput } = useDecimalFormat();
  const [viewTab, setViewTab] = useState('analytics');

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/api/data/items', { params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir } }); setRecords(res.data.data); setTotal(res.data.total); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load items'); }
    setLoading(false);
  }, [search, page, pageSize, sortState]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSegments = useCallback(async () => {
    try { const res = await api.get('/api/system/account-code-segments'); setSegments(res.data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSegments(); }, [loadSegments]);

  // Auto-open edit dialog when ?edit=ITEM_CODE is in URL (e.g., returning from inventory)
  useEffect(() => {
    const editCode = searchParams.get('edit');
    if (editCode) {
      // Fetch the item directly to ensure we find it regardless of current page
      api.get(`/api/data/items/${editCode}`).then(r => {
        if (r.data) {
          openEdit(r.data);
        }
      }).catch(() => {});
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const openCreate = () => { setForm(EMPTY_ITEM); setIsEdit(false); setItemSuppliers([]); setImagePreview(null); setImageFile(null); setModalOpen(true); };
  const openEdit = async r => {
    setForm({ ...r }); setIsEdit(true);
    setImagePreview(r.image_path ? `${BACKEND_URL}/api/data/items/${r.item_code}/image` : null);
    setImageFile(null);
    try { const res = await api.get(`/api/data/items/${r.item_code}/suppliers`); setItemSuppliers(res.data); } catch {}
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, _id, created_at, created_by, updated_at, updated_by, image_path, suppliers, ...payload } = form;
      if (isEdit) await api.put(`/api/data/items/${form.item_code}`, payload);
      else await api.post('/api/data/items', payload);
      if (imageFile && form.item_code) {
        const fd = new FormData(); fd.append('file', imageFile);
        await api.post(`/api/data/items/${form.item_code}/image`, fd);
      }
      toast.success(isEdit ? 'Updated' : 'Created'); setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteItem = async () => {
    if (!await globalConfirm('Delete this item?')) return;
    try { await api.delete(`/api/data/items/${form.item_code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const handleImageSelect = e => {
    const file = e.target.files?.[0]; if (!file) return;
    setImageFile(file);
    const reader = new FileReader(); reader.onload = ev => setImagePreview(ev.target.result); reader.readAsDataURL(file);
  };
  const handleImageDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('ring-2', 'ring-primary', 'bg-primary/5');
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader(); reader.onload = ev => setImagePreview(ev.target.result); reader.readAsDataURL(file);
    }
  };

  const openSupplierEdit = (s) => {
    setSupplierForm({
      ...s,
      unit_price: fmtInput(s.unit_price),
      discount: fmtInput(s.discount),
      sales_tax: fmtInput(s.sales_tax),
    });
    setEditingSupplier(s.id || s._id);
    setSupplierModalOpen(true);
  };
  const openSupplierCreate = () => { setSupplierForm({ ...EMPTY_SUPPLIER }); setEditingSupplier(null); setSupplierModalOpen(true); };

  const saveSupplier = async () => {
    try {
      const { _id, id, item_code, created_at, ...payload } = supplierForm;
      if (editingSupplier) await api.put(`/api/data/items/${form.item_code}/suppliers/${editingSupplier}`, payload);
      else await api.post(`/api/data/items/${form.item_code}/suppliers`, payload);
      const res = await api.get(`/api/data/items/${form.item_code}/suppliers`);
      setItemSuppliers(res.data); toast.success('Supplier saved'); setSupplierModalOpen(false);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to save supplier')); }
  };

  const deleteSupplier = async (supplierId) => {
    if (!await globalConfirm('Remove this supplier?')) return;
    try { await api.delete(`/api/data/items/${form.item_code}/suppliers/${supplierId}`); const res = await api.get(`/api/data/items/${form.item_code}/suppliers`); setItemSuppliers(res.data); toast.success('Removed'); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed'); }
  };



  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('items', r.item_code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => (rec === r) ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  const itemDefaultCols = useMemo(() => [
    { key: 'item_code', label: 'Item Code', width: 130, sortField: 'item_code', visible: true },
    { key: 'outline', label: 'Outline', width: 200, sortField: 'outline', visible: true },
    { key: 'category_code', label: 'Category', width: 120, sortField: 'category_code', visible: true },
    { key: 'item_type', label: 'Item Type', width: 120, sortField: 'item_type', visible: true },
    { key: 'active', label: 'Active', width: 80, sortField: 'active', visible: true },
    { key: 'stock_item', label: 'Stock Item', width: 100, sortField: 'stock_item', visible: true },
    { key: 'batch_tracked', label: 'Batch', width: 90, sortField: 'batch_tracked', visible: true },
    { key: 'serial_tracked', label: 'Serial', width: 90, sortField: 'serial_tracked', visible: true },
  ], []);

  const renderItemCell = useCallback((col, r) => {
    switch (col.key) {
      case 'item_code': return <span className="font-mono text-primary font-medium text-sm">{r.item_code}</span>;
      case 'outline': return <span className="text-sm max-w-[200px] truncate block">{r.outline}</span>;
      case 'category_code': return <Badge variant="secondary" className="text-xs">{r.category_code}</Badge>;
      case 'item_type': return <span className="text-sm">{r.item_type}</span>;
      case 'active': return <ActiveBadge active={r.active} />;
      case 'stock_item': return <ActiveBadge active={r.stock_item} />;
      case 'batch_tracked': return r.batch_tracked ? <Badge className="bg-amber-100 text-amber-700 text-[9px]"><Package className="h-2.5 w-2.5 inline mr-0.5" />Batch</Badge> : <span className="text-muted-foreground text-xs">—</span>;
      case 'serial_tracked': return r.serial_tracked ? <Badge className="bg-blue-100 text-blue-700 text-[9px]"><QrCode className="h-2.5 w-2.5 inline mr-0.5" />Serial</Badge> : <span className="text-muted-foreground text-xs">—</span>;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, []);

  return (
    <AppShell>
      <PageHeader title="Items" description={`Total records: ${total}`} helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Button size="sm" variant="outline" onClick={() => toast.info('Group Items (Bill of Materials) - coming soon')}><Plus className="h-4 w-4 mr-1" /> Group Items</Button>
          {canWriteData && <Button size="sm" onClick={openCreate} data-testid="items-add-button"><Plus className="h-4 w-4 mr-1" /> Add New</Button>}
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="items-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="items-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="items-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Items
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="items-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (<DataFilesAnalytics {...itemsConfig} onCardClick={() => setViewTab('list')} />) : viewTab === 'reports' ? (
        <ModuleReports reports={ITEMS_REPORTS} apiPrefix="/api/module-reports/data" module="Items" />
      ) : (<>
      <DataTableToolbar searchValue={search} onSearch={v => { setSearch(v); setPage(1); }} onClear={() => { setSearch(''); setPage(1); }} searchPlaceholder="Search items..." toolbarSlotId="items-listing-toolbar" />

      <div className="rounded-xl border bg-card">
        {loading ? <div className="py-12"><LoadingSpinner /></div> : records.length === 0 ? <EmptyState title="No items" action={<Button size="sm" onClick={openCreate}>Add Item</Button>} /> : (
          <InteractiveTable
            tableId="items-listing"
            toolbarPortalId="items-listing-toolbar"
            defaultCols={itemDefaultCols}
            data={records}
            renderCell={renderItemCell}
            onRowClick={(row) => openEdit(row)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="item_code"
          />
        )}
        <div className="px-4"><PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} /></div>
      </div>
      </>)}

      {/* Item Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Items - {isEdit ? `Edit - ${form.item_code}` : 'Add New'}
              <div className="flex items-center gap-2 mr-6"><span className="text-sm font-normal text-muted-foreground">Active</span><Switch checked={form.active} onCheckedChange={v => f('active', v)} /></div>
            </DialogTitle>
          </DialogHeader>

          {/* Stock Item Banner — prominent at top */}
          <div className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 mb-3 ${form.stock_item ? 'bg-blue-50 border-blue-200' : 'bg-muted/30 border-muted'}`}>
            <div className="flex items-center gap-3">
              <Boxes className={`h-5 w-5 ${form.stock_item ? 'text-blue-600' : 'text-muted-foreground'}`} />
              <div>
                <p className={`text-sm font-semibold ${form.stock_item ? 'text-blue-800' : 'text-muted-foreground'}`}>Inventory Tracked Item</p>
                <p className="text-[10px] text-muted-foreground">{form.stock_item ? 'This item is tracked in inventory control — stock levels, locations, and movements are managed.' : 'Enable to track this item in the inventory control system.'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {form.stock_item && form.item_code && isEdit && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => navigate(`/inventory?item=${form.item_code}`)}>
                  <Boxes className="h-3 w-3 mr-1" /> View Inventory
                </Button>
              )}
              <Switch checked={form.stock_item} onCheckedChange={v => f('stock_item', v)} id="stock-item" />
            </div>
          </div>

          {/* Batch Tracking toggle — only visible when stock_item is enabled */}
          {form.stock_item && (
            <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border-2 mb-3 ${form.batch_tracked ? 'bg-amber-50 border-amber-200' : 'bg-muted/20 border-muted'}`}>
              <div className="flex items-center gap-3">
                <Package className={`h-4 w-4 ${form.batch_tracked ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-xs font-semibold ${form.batch_tracked ? 'text-amber-800' : 'text-muted-foreground'}`}>Batch Tracked</p>
                  <p className="text-[10px] text-muted-foreground">{form.batch_tracked ? 'Batch details (batch #, expiry, manufacturer) are required when receiving this item.' : 'Enable to require batch/lot details during goods receipt.'}</p>
                </div>
              </div>
              <Switch checked={form.batch_tracked} onCheckedChange={v => f('batch_tracked', v)} id="batch-tracked" data-testid="batch-tracked-toggle" />
            </div>
          )}

          {/* Serial Tracking toggle — only visible when batch_tracked is enabled */}
          {form.stock_item && form.batch_tracked && (
            <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border-2 mb-3 ${form.serial_tracked ? 'bg-blue-50 border-blue-200' : 'bg-muted/20 border-muted'}`}>
              <div className="flex items-center gap-3">
                <QrCode className={`h-4 w-4 ${form.serial_tracked ? 'text-blue-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-xs font-semibold ${form.serial_tracked ? 'text-blue-800' : 'text-muted-foreground'}`}>Serial Number Tracked</p>
                  <p className="text-[10px] text-muted-foreground">{form.serial_tracked ? 'Each unit requires a unique serial number at point of receipt.' : 'Enable to require individual serial numbers for every unit in each batch.'}</p>
                </div>
              </div>
              <Switch checked={form.serial_tracked} onCheckedChange={v => f('serial_tracked', v)} id="serial-tracked" data-testid="serial-tracked-toggle" />
            </div>
          )}

          <Tabs defaultValue="details">
            <TabsList><TabsTrigger value="details">Item Details</TabsTrigger><TabsTrigger value="suppliers">Supplier Details ({itemSuppliers.length})</TabsTrigger><TabsTrigger value="price-history">Price History</TabsTrigger></TabsList>
            <TabsContent value="details" className="pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Item Code *</Label><Input value={form.item_code} onChange={e => f('item_code', e.target.value)} disabled={isEdit} /></div>
                    <div className="space-y-1.5"><Label>Outline (Short Name) *</Label><Input value={form.outline} onChange={e => f('outline', e.target.value)} /></div>
                    <div className="space-y-1.5">
                      <Label>Item Type</Label>
                      <select value={form.item_type} onChange={e => f('item_type', e.target.value)} className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">
                        {ITEM_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <DataLookupField value={form.category_code} onChange={v => f('category_code', v)}
                      apiEndpoint="/api/data/categories" label="Category Code" dataTestId="item-category-code" />
                    <DataLookupField value={form.account_code_4} onChange={v => f('account_code_4', v)}
                      apiEndpoint="/api/account-codes/4" label={expLabels.exp_code_4 || 'Expense Code 4'} dataTestId="item-ac4" />
                    <DataLookupField value={form.account_code_5} onChange={v => f('account_code_5', v)}
                      apiEndpoint="/api/account-codes/5" label={expLabels.exp_code_5 || 'Expense Code 5'} dataTestId="item-ac5" />
                    <DataLookupField value={form.account_code_6} onChange={v => f('account_code_6', v)}
                      apiEndpoint="/api/account-codes/6" label={expLabels.exp_code_6 || 'Expense Code 6'} dataTestId="item-ac6" />
                    <div className="space-y-1.5"><Label>UPC</Label><Input value={form.upc} onChange={e => f('upc', e.target.value)} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={e => f('description', e.target.value)} rows={2} /></div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Item Image</Label>
                    <div className="mt-1.5 border-2 border-dashed rounded-xl p-3 flex flex-col items-center gap-2 cursor-pointer hover:border-primary"
                      onClick={() => document.getElementById('item-img-input')?.click()}
                      onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-primary', 'bg-primary/5'); }}
                      onDragLeave={e => { e.currentTarget.classList.remove('ring-2', 'ring-primary', 'bg-primary/5'); }}
                      onDrop={handleImageDrop}>
                      {imagePreview ? <img src={imagePreview} alt="Item" className="max-h-24 object-contain" /> : (
                        <>
                          <Image className="h-8 w-8 text-muted-foreground/40" />
                          <p className="text-xs text-muted-foreground">Drag & drop image here, or click to browse</p>
                        </>
                      )}
                      <Button type="button" size="sm" variant="outline" onClick={e => { e.stopPropagation(); document.getElementById('item-img-input')?.click(); }}>+ Update Image</Button>
                      {imagePreview && <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={async e => {
                        e.stopPropagation();
                        if (isEdit && form.item_code) {
                          try { await api.delete(`/api/data/items/${form.item_code}/image`); } catch {}
                        }
                        setImagePreview(null); setImageFile(null);
                      }}>Delete Image</Button>}
                    </div>
                    <input id="item-img-input" type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                  </div>
                  {isEdit && (
                    <div>
                      <Label>QR Code</Label>
                      <div className="mt-1.5 border rounded-xl p-3 flex flex-col items-center gap-2">
                        <img src={`${BACKEND_URL}/api/data/items/${form.item_code}/qrcode`} alt={`QR code for ${form.item_code}`} className="w-28 h-28" />
                        <p className="text-xs text-muted-foreground font-mono">{form.item_code}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="suppliers" className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <Button size="sm" onClick={openSupplierCreate}><Plus className="h-4 w-4 mr-1" /> Add Supplier</Button>
                <span className="text-xs text-muted-foreground"><span className="inline-block w-2 h-2 rounded-full bg-primary mr-1"></span>Indicates Main Supplier</span>
              </div>
              {itemSuppliers.length === 0 ? <EmptyState title="No suppliers linked" /> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Supplier Code</TableHead><TableHead>Supplier Name</TableHead><TableHead>Part Number</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Price</TableHead><TableHead>Currency</TableHead><TableHead>Discount</TableHead><TableHead>Sales Tax</TableHead><TableHead>CO₂e</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {itemSuppliers.map((s, i) => (
                      <TableRow key={i} className="cursor-pointer" onClick={() => openSupplierEdit(s)}>
                        <TableCell className="font-mono text-xs">{s.is_main_supplier && <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1"></span>}{s.supplier_code}</TableCell>
                        <TableCell className="text-sm">{s.supplier_name}</TableCell>
                        <TableCell className="text-xs font-mono">{s.suppliers_part_number}</TableCell>
                        <TableCell className="text-xs">{s.purchase_unit}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtVal(s.unit_price)}</TableCell>
                        <TableCell className="text-xs">{s.currency || 'USD'}</TableCell>
                        <TableCell className="text-xs">{s.discount}%</TableCell>
                        <TableCell className="text-xs">{s.sales_tax}%</TableCell>
                        <TableCell className="text-xs">{s.co2e}</TableCell>
                        <TableCell><Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); deleteSupplier(s.id); }} aria-label="Remove"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
            <TabsContent value="price-history" className="pt-4">
              <PriceHistoryTab itemCode={form.item_code} />
            </TabsContent>
          </Tabs>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && <Button variant="destructive" onClick={deleteItem}>Delete</Button>}
            <Button onClick={save} disabled={saving || !form.item_code || !form.outline}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Edit Modal */}
      <Dialog open={supplierModalOpen} onOpenChange={setSupplierModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Item Supplier</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Supplier Code</Label>
                <SupplierLookupField
                  value={supplierForm.supplier_code}
                  onChange={(code, name) => setSupplierForm(p => ({ ...p, supplier_code: code, supplier_name: name || p.supplier_name }))}
                /></div>
              <div className="space-y-1.5"><Label>Supplier Name</Label><Input value={supplierForm.supplier_name} disabled className="bg-muted/40" /></div>
              <div className="space-y-1.5"><Label>Part Number</Label><Input value={supplierForm.suppliers_part_number} onChange={e => setSupplierForm(p => ({ ...p, suppliers_part_number: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Purchase Unit</Label><Input value={supplierForm.purchase_unit} onChange={e => setSupplierForm(p => ({ ...p, purchase_unit: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Pack Size</Label><Input type="number" value={supplierForm.pack_size} onChange={e => setSupplierForm(p => ({ ...p, pack_size: parseFloat(e.target.value) || 1 }))} /></div>
              <div className="space-y-1.5"><Label>Unit Price</Label><Input type="number" step="0.0001" value={supplierForm.unit_price} onChange={e => setSupplierForm(p => ({ ...p, unit_price: e.target.value }))} onBlur={() => setSupplierForm(p => ({...p, unit_price: fmtInput(p.unit_price)}))} /></div>
              <div className="space-y-1.5"><Label>Discount %</Label><Input type="number" step="0.0001" value={supplierForm.discount} onChange={e => setSupplierForm(p => ({ ...p, discount: e.target.value }))} onBlur={() => setSupplierForm(p => ({...p, discount: fmtInput(p.discount)}))} /></div>
              <div className="space-y-1.5"><Label>Sales Tax %</Label><Input type="number" step="0.0001" value={supplierForm.sales_tax} onChange={e => setSupplierForm(p => ({ ...p, sales_tax: e.target.value }))} onBlur={() => setSupplierForm(p => ({...p, sales_tax: fmtInput(p.sales_tax)}))} /></div>
              <div className="space-y-1.5"><Label>Lead Time (days)</Label><Input type="number" value={supplierForm.lead_time} onChange={e => setSupplierForm(p => ({ ...p, lead_time: parseInt(e.target.value) || 0 }))} /></div>
              <div className="space-y-1.5"><Label>CO₂e</Label><Input type="number" step="0.01" value={supplierForm.co2e} onChange={e => setSupplierForm(p => ({ ...p, co2e: parseFloat(e.target.value) || 0 }))} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={supplierForm.is_main_supplier} onCheckedChange={v => setSupplierForm(p => ({ ...p, is_main_supplier: v }))} id="main-supp" />
              <Label htmlFor="main-supp">Main Supplier</Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSupplierModalOpen(false)}>Cancel</Button>
            <Button onClick={saveSupplier}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


function PriceHistoryTab({ itemCode }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!itemCode) { setLoading(false); return; }
    api.get(`/api/items-suppliers/items/${itemCode}/price-history`)
      .then(r => setHistory(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [itemCode]);

  if (loading) return <p className="text-xs text-muted-foreground text-center py-4">Loading...</p>;
  if (history.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">No price history for this item.</p>;

  const typeLabels = { manual: 'Manual Edit', supplier_request: 'Supplier Request', po_update: 'PO Update', import: 'Import', bulk_update: 'Bulk Update' };
  const typeColors = { manual: 'bg-blue-100 text-blue-700', supplier_request: 'bg-violet-100 text-violet-700', po_update: 'bg-amber-100 text-amber-700', import: 'bg-slate-100 text-slate-600', bulk_update: 'bg-rose-100 text-rose-700' };

  return (
    <div className="border rounded overflow-hidden">
      <table className="w-full text-xs">
        <thead><tr className="bg-muted/50 border-b">
          <th className="text-left px-3 py-2 font-semibold">Date</th>
          <th className="text-right px-3 py-2 font-semibold">Old Price</th>
          <th className="text-right px-3 py-2 font-semibold">New Price</th>
          <th className="text-right px-3 py-2 font-semibold">% Change</th>
          <th className="text-left px-3 py-2 font-semibold">Source</th>
          <th className="text-left px-3 py-2 font-semibold">Changed By</th>
          <th className="text-left px-3 py-2 font-semibold">Reason</th>
        </tr></thead>
        <tbody>
          {history.map((h, i) => (
            <tr key={i} className="border-b">
              <td className="px-3 py-2">{h.changed_at ? new Date(h.changed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : ''}</td>
              <td className="px-3 py-2 text-right font-mono">${Number(h.old_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="px-3 py-2 text-right font-mono font-semibold">${Number(h.new_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className={`px-3 py-2 text-right font-mono ${h.pct_change > 0 ? 'text-red-600' : h.pct_change < 0 ? 'text-emerald-600' : ''}`}>{h.pct_change > 0 ? '+' : ''}{h.pct_change}%</td>
              <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColors[h.change_type] || 'bg-slate-100'}`}>{typeLabels[h.change_type] || h.change_type}</span></td>
              <td className="px-3 py-2 text-slate-600">{h.changed_by}</td>
              <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate" title={h.reason || h.order_number || ''}>{h.reason || (h.order_number ? `From ${h.order_number}` : '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
